import React, { useState, useEffect } from "react";
import { useNotifications } from "../../../contexts/notification";
import styled from "styled-components";

// Styled components to match company dashboard aesthetic
const EvaluationContainer = styled.div`
  padding: 1.5rem;
  color: rgba(255, 255, 255, 0.9);
  animation: fadeIn 0.5s ease-out;
  background: linear-gradient(
    135deg,
    rgba(15, 23, 42, 0.8) 0%,
    rgba(31, 41, 55, 0.9) 100%
  );
  border-radius: 16px;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const EvaluationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 1rem;
`;

const CloseButton = styled.button`
  background-color: transparent;
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.5rem;
  padding: 0.25rem 0.5rem;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: rgba(255, 255, 255, 1);
    transform: scale(1.1);
  }
`;

const EvaluationTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
`;

const EvaluationCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    box-shadow: 0 8px 32px rgba(31, 38, 135, 0.15);
  }

  h3 {
    margin-top: 0;
    margin-bottom: 0.75rem;
    font-size: 1.25rem;
    color: rgba(255, 255, 255, 0.95);
  }

  p {
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 1.5rem;
    font-size: 0.9rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.8);
  }
`;

const SliderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const RangeInput = styled.input`
  flex: 1;
  -webkit-appearance: none;
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  outline: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: rgba(99, 102, 241, 0.9);
    cursor: pointer;
    border: 2px solid rgba(255, 255, 255, 0.8);
    transition: all 0.2s ease;
  }

  &::-webkit-slider-thumb:hover {
    background: rgba(99, 102, 241, 1);
    transform: scale(1.1);
  }
`;

const NumberInput = styled.input`
  width: 60px;
  padding: 0.5rem;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.1);
  color: white;
  text-align: center;
`;

const ScoreDisplay = styled.span`
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  min-width: 80px;
  text-align: center;

  &.excellent {
    background-color: rgba(16, 185, 129, 0.3);
    color: rgba(16, 185, 129, 1);
    border: 1px solid rgba(16, 185, 129, 0.5);
  }

  &.good {
    background-color: rgba(59, 130, 246, 0.3);
    color: rgba(59, 130, 246, 1);
    border: 1px solid rgba(59, 130, 246, 0.5);
  }

  &.average {
    background-color: rgba(245, 158, 11, 0.3);
    color: rgba(245, 158, 11, 1);
    border: 1px solid rgba(245, 158, 11, 0.5);
  }

  &.needs-improvement {
    background-color: rgba(239, 68, 68, 0.3);
    color: rgba(239, 68, 68, 1);
    border: 1px solid rgba(239, 68, 68, 0.5);
  }
`;

const OverallScoreContainer = styled.div`
  margin-top: 1.5rem;
  text-align: center;

  h4 {
    font-size: 1.1rem;
    margin-bottom: 1rem;
    color: rgba(255, 255, 255, 0.9);
  }
`;

const OverallScoreDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  margin-bottom: 0.75rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid
    ${(props) => (props.error ? "#f87171" : "rgba(255, 255, 255, 0.2)")};
  background: rgba(255, 255, 255, 0.08);
  color: white;
  resize: vertical;

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }

  &:focus {
    outline: none;
    border-color: ${(props) =>
      props.error ? "#f87171" : "rgba(99, 102, 241, 0.5)"};
    box-shadow: 0 0 0 2px
      ${(props) =>
        props.error ? "rgba(248, 113, 113, 0.25)" : "rgba(99, 102, 241, 0.25)"};
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  min-width: 120px;
`;

const PrimaryButton = styled(Button)`
  background: linear-gradient(
    135deg,
    rgba(99, 102, 241, 0.8) 0%,
    rgba(79, 70, 229, 0.9) 100%
  );
  color: white;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};

  &:hover {
    transform: ${(props) => (props.disabled ? "none" : "translateY(-2px)")};
    box-shadow: ${(props) =>
      props.disabled
        ? "0 4px 12px rgba(99, 102, 241, 0.3)"
        : "0 6px 16px rgba(99, 102, 241, 0.4)"};
  }
`;

const SecondaryButton = styled(Button)`
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.2);

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 255, 255, 0.1);
  }
  
  &:active {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(255, 255, 255, 0.1);
  }
`;

const DangerButton = styled(Button)`
  background: rgba(239, 68, 68, 0.1);
  color: rgba(239, 68, 68, 0.9);
  border: 1px solid rgba(239, 68, 68, 0.2);

  &:hover {
    background: rgba(239, 68, 68, 0.2);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.2);
    color: rgba(239, 68, 68, 1);
  }
  
  &:active {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.2);
  }
`;

const InfoText = styled.p`
  text-align: center;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 0;
`;

const ErrorMessage = styled.div`
  color: #f87171;
  font-size: 0.8rem;
  margin-top: 0.25rem;
  font-weight: 500;
`;

const InternEvaluation = ({
  student,
  onClose,
  onSave,
  existingEvaluation = null,
  onDelete = null,
}) => {
  const { addNotification } = useNotifications();
  const [evaluation, setEvaluation] = useState({
    technical: existingEvaluation?.technical || 75,
    communication: existingEvaluation?.communication || 75,
    problemSolving: existingEvaluation?.problemSolving || 75,
    teamwork: existingEvaluation?.teamwork || 75,
    overall: existingEvaluation?.overall || 75,
    strengths: existingEvaluation?.strengths || "",
    improvements: existingEvaluation?.improvements || "",
    comments: existingEvaluation?.comments || "",
  });
  const [errors, setErrors] = useState({
    strengths: false,
    improvements: false,
    comments: false,
  });

  // Calculate overall score whenever individual scores change
  useEffect(() => {
    const { technical, communication, problemSolving, teamwork } = evaluation;
    const overallScore = Math.round(
      (technical + communication + problemSolving + teamwork) / 4
    );
    setEvaluation((prev) => ({ ...prev, overall: overallScore }));
  }, [
    evaluation.technical,
    evaluation.communication,
    evaluation.problemSolving,
    evaluation.teamwork,
  ]);
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const numValue =
      name !== "strengths" && name !== "improvements" && name !== "comments"
        ? Math.min(100, Math.max(0, parseInt(value) || 0))
        : value;

    // Clear validation error when user types in a field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: false,
      });
    }

    setEvaluation({
      ...evaluation,
      [name]: numValue,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate all text fields are filled
    const newErrors = {
      strengths: !evaluation.strengths.trim(),
      improvements: !evaluation.improvements.trim(),
      comments: !evaluation.comments.trim(),
    };

    const hasErrors = Object.values(newErrors).some((error) => error);

    if (hasErrors) {
      setErrors(newErrors);
      // Scroll to first error
      const firstErrorField = Object.keys(newErrors).find(
        (key) => newErrors[key]
      );
      document
        .getElementById(firstErrorField)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    onSave(evaluation);
    addNotification({
      title: "Evaluation Saved",
      message: `Evaluation for ${student.studentName} has been saved successfully.`,
      type: "success",
    });
  };

  const getScoreClass = (score) => {
    if (score >= 90) return "excellent";
    if (score >= 75) return "good";
    if (score >= 60) return "average";
    return "needs-improvement";
  };
  return (
    <EvaluationContainer>
      <EvaluationHeader>
        <EvaluationTitle>
          Student Evaluation: {student.studentName}
        </EvaluationTitle>
        <CloseButton onClick={onClose}>&times;</CloseButton>
      </EvaluationHeader>

      <form onSubmit={handleSubmit}>
        <EvaluationCard>
          <h3>Performance Ratings</h3>
          <p>
            Rate the intern's performance on a scale of 0-100 for each category.
          </p>

          <FormGroup>
            <label htmlFor="technical">Technical Skills:</label>
            <SliderContainer>
              <RangeInput
                type="range"
                id="technical"
                name="technical"
                min="0"
                max="100"
                value={evaluation.technical}
                onChange={handleInputChange}
              />
              <NumberInput
                type="number"
                name="technical"
                min="0"
                max="100"
                value={evaluation.technical}
                onChange={handleInputChange}
              />
              <ScoreDisplay className={getScoreClass(evaluation.technical)}>
                {evaluation.technical}/100
              </ScoreDisplay>
            </SliderContainer>
          </FormGroup>

          <FormGroup>
            <label htmlFor="communication">Communication:</label>
            <SliderContainer>
              <RangeInput
                type="range"
                id="communication"
                name="communication"
                min="0"
                max="100"
                value={evaluation.communication}
                onChange={handleInputChange}
              />
              <NumberInput
                type="number"
                name="communication"
                min="0"
                max="100"
                value={evaluation.communication}
                onChange={handleInputChange}
              />
              <ScoreDisplay className={getScoreClass(evaluation.communication)}>
                {evaluation.communication}/100
              </ScoreDisplay>
            </SliderContainer>
          </FormGroup>

          <FormGroup>
            <label htmlFor="problemSolving">Problem Solving:</label>
            <SliderContainer>
              <RangeInput
                type="range"
                id="problemSolving"
                name="problemSolving"
                min="0"
                max="100"
                value={evaluation.problemSolving}
                onChange={handleInputChange}
              />
              <NumberInput
                type="number"
                name="problemSolving"
                min="0"
                max="100"
                value={evaluation.problemSolving}
                onChange={handleInputChange}
              />
              <ScoreDisplay
                className={getScoreClass(evaluation.problemSolving)}
              >
                {evaluation.problemSolving}/100
              </ScoreDisplay>
            </SliderContainer>
          </FormGroup>

          <FormGroup>
            <label htmlFor="teamwork">Teamwork:</label>
            <SliderContainer>
              <RangeInput
                type="range"
                id="teamwork"
                name="teamwork"
                min="0"
                max="100"
                value={evaluation.teamwork}
                onChange={handleInputChange}
              />
              <NumberInput
                type="number"
                name="teamwork"
                min="0"
                max="100"
                value={evaluation.teamwork}
                onChange={handleInputChange}
              />
              <ScoreDisplay className={getScoreClass(evaluation.teamwork)}>
                {evaluation.teamwork}/100
              </ScoreDisplay>
            </SliderContainer>
          </FormGroup>

          <OverallScoreContainer>
            <h4>Overall Score</h4>
            <OverallScoreDisplay>
              <ScoreDisplay className={getScoreClass(evaluation.overall)}>
                {evaluation.overall}/100
              </ScoreDisplay>
            </OverallScoreDisplay>
            <InfoText>
              Overall score is calculated as the average of the four categories
              above.
            </InfoText>
          </OverallScoreContainer>
        </EvaluationCard>
        <EvaluationCard>
          <h3>Qualitative Feedback</h3>{" "}
          <FormGroup>
            <label htmlFor="strengths">Strengths:</label>
            <TextArea
              id="strengths"
              name="strengths"
              rows="3"
              value={evaluation.strengths}
              onChange={handleInputChange}
              placeholder="What were the intern's key strengths?"
              error={errors.strengths}
            ></TextArea>
            {errors.strengths && (
              <ErrorMessage>This field is required</ErrorMessage>
            )}
          </FormGroup>
          <FormGroup>
            <label htmlFor="improvements">Areas for Improvement:</label>
            <TextArea
              id="improvements"
              name="improvements"
              rows="3"
              value={evaluation.improvements}
              onChange={handleInputChange}
              placeholder="What areas could the intern improve upon?"
              error={errors.improvements}
            ></TextArea>
            {errors.improvements && (
              <ErrorMessage>This field is required</ErrorMessage>
            )}
          </FormGroup>
          <FormGroup>
            <label htmlFor="comments">Additional Comments:</label>
            <TextArea
              id="comments"
              name="comments"
              rows="3"
              value={evaluation.comments}
              onChange={handleInputChange}
              placeholder="Any additional comments or feedback for the intern"
              error={errors.comments}
            ></TextArea>
            {errors.comments && (
              <ErrorMessage>This field is required</ErrorMessage>
            )}
          </FormGroup>
        </EvaluationCard>{" "}
        <ButtonContainer>
          <SecondaryButton type="button" onClick={onClose}>
            Cancel
          </SecondaryButton>
          {existingEvaluation && onDelete && (
            <DangerButton 
              type="button" 
              onClick={() => {
                const confirmDelete = window.confirm(`Are you sure you want to delete the evaluation for ${student.studentName}? This action cannot be undone.`);
                if (confirmDelete) {
                  onDelete(student);
                }
              }}
            >
              Delete Evaluation
            </DangerButton>
          )}
          <PrimaryButton
            type="submit"
            disabled={
              !evaluation.strengths.trim() ||
              !evaluation.improvements.trim() ||
              !evaluation.comments.trim()
            }
          >
            Save Evaluation
          </PrimaryButton>
        </ButtonContainer>
      </form>
    </EvaluationContainer>
  );
};

export default InternEvaluation;
